import {
  createSlice,
  createAsyncThunk,
  type ActionReducerMapBuilder,
  type AsyncThunk,
} from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { fetchData } from './api';
import { currentTimeZone } from '../../_helpers/time_zone';
import { lensParams, selectLensGroupId } from './groups_slice';

export type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/**
 * A support_resources row as the API serializes it (every column, and null for
 * an empty one), or an entry from SupportResources/default_resources.json
 * (which leaves fields out and adds src/alt/hours).
 */
export interface PersonalSupportResource {
  id?: number;
  practice_group_id?: number;
  resource_name?: string | null;
  resource_description?: string | null;
  resource_url?: string | null;
  resource_url_description?: string | null;
  phone?: string | null;
  phone_description?: string | null;
  chat_url?: string | null;
  chat_url_description?: string | null;
  email?: string | null;
  email_description?: string | null;
  created_at?: string;
  updated_at?: string;
  src?: string;
  alt?: string;
  image_style?: Record<string, unknown>;
  [key: string]: unknown;
}

/** GET /api/my_burnout_level. Both fields are null for someone who has never
 *  answered the level question. */
export interface MyBurnoutLevel {
  burnout_level: number | null;
  responded_at: string | null;
  support_resource: PersonalSupportResource | null;
}

/*
-------------------------------
MY METRICS RESPONSE TYPES
Mirror the my_metrics build contract field for field. Every endpoint reads only
the signed-in user; `null` means "no value to show" (no reading yet, or a group
value withheld because fewer than `min_others` colleagues contributed), never 0.
-------------------------------
*/

/** A calendar month key, 'YYYY-MM'. Personal months are in the viewer's time
 *  zone; group months are fixed UTC months. */
export type MonthKey = string;

/** An ISO-8601 UTC timestamp. Format it in local time before display. */
export type IsoDateTime = string;

export interface MyBurnoutLevelChoice {
  level: number;
  text: string;
}

export interface MyBurnoutCurrent {
  level: number;
  responded_at: IsoDateTime;
  /** Same rule and shape as GET /api/my_burnout_level: present at level 5 only. */
  support_resource: PersonalSupportResource | null;
}

export interface MyBurnoutTrendPoint {
  month: MonthKey;
  /** The average of that month's readings, to one decimal; null when there
   *  was none. */
  level: number | null;
  /** The median of each other's monthly average, to one decimal. Always null
   *  for the current month and below the minimum group size. */
  group_median: number | null;
}

/** One of the viewer's own level answers inside the 12-month window. The
 *  monthly `trend` level is the average of these in its month. */
export interface MyBurnoutReading {
  responded_at: IsoDateTime;
  /** 1-5. */
  level: number;
  /** The viewer's-time-zone month it falls in, one of `months`. */
  month: MonthKey;
}

/**
 * How many others stand behind a released group value, as a band. The API
 * never returns an exact group count; copy must not imply one ("5-9 others",
 * never "7 others"). The lowest band starts at `min_others`.
 */
export type GroupRespondentsBand = '5-9' | '10-19' | '20-49' | '50+';

export interface MyBurnoutHighlightMonth {
  month: MonthKey;
  level: number;
}

export interface MyBurnoutHardestMonth extends MyBurnoutHighlightMonth {
  /** Categories named in any in-app stressor assessment that month; may be empty. */
  stressors: string[];
}

export interface MyBurnoutHighlights {
  best: MyBurnoutHighlightMonth | null;
  hardest: MyBurnoutHardestMonth | null;
  months_checked_in: number;
  months_possible: number;
}

export type MyActivityEventType =
  | 'series_started'
  | 'series_finished'
  | 'ce_claimed'
  | 'top_stressor_changed';

export interface MyActivityEvent {
  type: MyActivityEventType;
  /** Series name, activity title, or the new #1 stressor. Content-derived: escape it. */
  label: string;
  occurred_at: IsoDateTime;
}

export interface MyActivityMonth {
  month: MonthKey;
  events: MyActivityEvent[];
}

export interface MyBurnoutCallout {
  series: string;
  start_month: MonthKey;
  end_month: MonthKey;
  months: number;
  from_level: number;
  to_level: number;
}

/** GET /api/my_metrics/burnout */
export interface MyBurnoutMetrics {
  time_zone: string;
  /** 12 keys, oldest first, ending with the current month. */
  months: MonthKey[];
  level_choices: MyBurnoutLevelChoice[];
  current: MyBurnoutCurrent | null;
  /** 12 entries aligned with `months`. */
  trend: MyBurnoutTrendPoint[];
  /** Every answer in the window, oldest first. */
  readings: MyBurnoutReading[];
  /** The band of the smallest per-month count of others behind any released
   *  median; null when no month's median is released. */
  group_respondents_band: GroupRespondentsBand | null;
  min_others: number;
  highlights: MyBurnoutHighlights;
  /** Only months with events; events sorted by occurred_at. */
  activity: MyActivityMonth[];
  callout: MyBurnoutCallout | null;
}

export interface MySteadyHand {
  earned: boolean;
  awarded_at: IsoDateTime | null;
}

/** One COUNTED check-in (CheckInStreakService's rules, the same ones the
 *  streak uses). */
export interface MyRecentCheckIn {
  at: IsoDateTime;
  /** Whole days (elapsed time, rounded down) since the previous counted
   *  check-in, even when that one is older than the list; null only for the
   *  first counted check-in ever. */
  gap_days: number | null;
  /** The gap before it was over 14 days, so a new run starts here. Can be true
   *  on the oldest item listed (a break before the first date shown). */
  starts_new_run: boolean;
}

/** GET /api/my_metrics/check_in */
export interface MyCheckIn {
  last_check_in_at: IsoDateTime | null;
  /** null means due now, or never answered. */
  next_opens_at: IsoDateTime | null;
  due_now: boolean;
  /** Additive to the contract. Whether the check-in due now (or, when none is,
   *  the one that opens at next_opens_at) asks the level question, the one the
   *  streak counts. The assessment link needs it as show_level. */
  level_due_with_next: boolean;
  stressors_due_with_next: boolean;
  streak: number;
  /** Additive to the contract. The last moment a check-in keeps the current
   *  streak going; null with no streak, or when the level question won't open
   *  before then. */
  streak_expires_at: IsoDateTime | null;
  /** The Steady Hand rule's target; never hardcode it on the client. */
  streak_target: number;
  /** The last up to `streak_target` counted check-ins, oldest first. */
  recent_check_ins: MyRecentCheckIn[];
  steady_hand: MySteadyHand;
}

export type MyStressorMovementKind = 'new' | 'held' | 'up' | 'down';

export interface MyStressorMovement {
  kind: MyStressorMovementKind;
  held_count: number | null;
  previous_rank: number | null;
}

export interface MyStressorNote {
  /** Raw text the user wrote. Escape it. */
  text: string;
  responded_at: IsoDateTime;
}

export interface MyStressor {
  rank: number;
  category_id: number;
  name: string;
  times_named: number;
  /** Percent of others in the group naming it, rounded to the nearest 10 and
   *  kept within 10..90. All or nothing: when any listed share is withheld,
   *  every share (and group_respondents_band) is null. */
  group_share: number | null;
  movement: MyStressorMovement;
  note: MyStressorNote | null;
}

export interface MyDroppedStressor {
  category_id: number;
  name: string;
  last_named_at: IsoDateTime;
}

export interface MyStressorPick {
  rank: number;
  category_id: number;
  name: string;
}

/** One in-app stressor assessment, for the history strip. */
export interface MyStressorAssessment {
  assessed_at: IsoDateTime;
  /** By rank, #1 first. */
  picks: MyStressorPick[];
}

/** GET /api/my_metrics/stressors */
export interface MyStressors {
  time_zone: string;
  assessments_count: number;
  /** Categories in the latest in-app stressor assessment, by slot rank. */
  stressors: MyStressor[];
  dropped: MyDroppedStressor[];
  /** The in-app assessments in the window, oldest first: at most the latest 12. */
  history: MyStressorAssessment[];
  /** The last of the 12 completed UTC months group shares are computed over. */
  window_end_month: MonthKey;
  /** Others behind the released shares, as a band; null when withheld. */
  group_respondents_band: GroupRespondentsBand | null;
  min_others: number;
}

export interface MyLearningComparison {
  window_end_month: MonthKey;
  my_finished: number;
  group_median_finished: number | null;
  /** my_finished - group_median_finished; may be negative, zero, or .5. */
  difference: number | null;
}

/** GET /api/my_metrics/learning */
export interface MyLearning {
  time_zone: string;
  months: number;
  microlearnings_finished: number;
  hours_watched: number;
  active_months: number;
  minutes_per_month: number;
  comparison: MyLearningComparison;
  min_others: number;
}

/*
-------------------------------
STATE
-------------------------------
*/

export interface AsyncEntry<T> {
  data: T | null;
  status: AsyncStatus;
  error: string | null;
  /** The request whose result this entry is waiting for; set while loading.
   *  A result from any other request (one started before a logout, say) is
   *  never written here. */
  requestId?: string;
  /** The group lens (selectLensGroupId) the data and any request in flight
   *  are for; absent without one. */
  lensGroupId?: number;
}

// Never persisted: the only store data written to localStorage is the
// authentication payload ('currentSession'), and logout resets this slice
// with the rest of the store.
export interface PersonalState {
  burnoutLevel: AsyncEntry<MyBurnoutLevel>;
  burnoutMetrics: AsyncEntry<MyBurnoutMetrics>;
  checkIn: AsyncEntry<MyCheckIn>;
  stressors: AsyncEntry<MyStressors>;
  learning: AsyncEntry<MyLearning>;
}

type PersonalKey = keyof PersonalState;
type PersonalPayload<K extends PersonalKey> = NonNullable<PersonalState[K]['data']>;
type PersonalThunkConfig = { state: RootState; rejectValue: string };

/** A thunk for one personal entry: no argument, resolves to its payload. */
export type PersonalThunk<T> = AsyncThunk<T, void, PersonalThunkConfig>;

const idleEntry = <T>(): AsyncEntry<T> => ({ data: null, status: 'idle', error: null });

const initialState: PersonalState = {
  burnoutLevel: idleEntry(),
  burnoutMetrics: idleEntry(),
  checkIn: idleEntry(),
  stressors: idleEntry(),
  learning: idleEntry(),
};

/*
-------------------------------
LOGOUT SAFETY
Logout is client-only and doesn't reload the page, so a request still in
flight when one person signs out can resolve after the store has been reset,
or after the next person has signed in on the same tab. Each thunk notes who
it was asked for, and a result that lands for anyone else is dropped: never
stored, never shown as an error.
-------------------------------
*/

/**
 * The rejection value for a response that arrived after the session it was
 * requested in had ended. The reducers ignore it apart from releasing the
 * entry's own loading state.
 */
export const SESSION_CHANGED = 'personal/session-changed';

interface SessionIdentity {
  userId: unknown;
  accessToken: string | null;
}

const sessionIdentity = (state: RootState): SessionIdentity => ({
  userId: state.authentication.user?.id ?? null,
  accessToken: state.authentication.accessToken ?? null,
});

const isSameSession = (a: SessionIdentity, b: SessionIdentity): boolean =>
  a.userId === b.userId && a.accessToken === b.accessToken;

const rejectionMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

// The API scopes every my_metrics endpoint to the signed-in user. The client
// sends the time zone and, only in a demo-lens build and only for a group
// picker, the group lens, which picks the demo story in the demo environment
// and never widens whose data is read. A production build sends the time zone
// alone.
const timeZoneFilters = (): Record<string, string> => {
  const tz = currentTimeZone();
  return tz ? { tz } : {};
};

interface PersonalThunkOptions<K extends PersonalKey> {
  key: K;
  typePrefix: string;
  endpoint: string;
  failureMessage: string;
  /** Send the viewer's time zone (every my_metrics endpoint buckets by it)
   *  and the group lens: the my_metrics endpoints answer for both. */
  sendTimeZone: boolean;
}

// fetchData takes no AbortSignal, so an abandoned request still runs to
// completion; the session check below is what keeps its result out.
const createPersonalThunk = <K extends PersonalKey>({
  key,
  typePrefix,
  endpoint,
  failureMessage,
  sendTimeZone,
}: PersonalThunkOptions<K>): PersonalThunk<PersonalPayload<K>> =>
  createAsyncThunk<PersonalPayload<K>, void, PersonalThunkConfig>(
    typePrefix,
    async (_, { getState, rejectWithValue }) => {
      const requestedFor = sessionIdentity(getState());
      const sessionChanged = () => !isSameSession(requestedFor, sessionIdentity(getState()));
      try {
        const filters = sendTimeZone ? { ...timeZoneFilters(), ...lensParams(selectLensGroupId(getState())) } : {};
        const payload = await fetchData({ endpoint, filters });
        if (sessionChanged()) return rejectWithValue(SESSION_CHANGED);
        // Every endpoint answers with a JSON object. Anything else (an empty
        // body, say) would render as missing figures, so it is a failure.
        if (payload === null || typeof payload !== 'object') return rejectWithValue(failureMessage);
        return payload as PersonalPayload<K>;
      } catch (error) {
        if (sessionChanged()) return rejectWithValue(SESSION_CHANGED);
        return rejectWithValue(rejectionMessage(error, failureMessage));
      }
    },
    {
      condition: (_, { getState }) => getState().personal[key].status !== 'loading',
    }
  );

export const fetchMyBurnoutLevel = createPersonalThunk({
  key: 'burnoutLevel',
  typePrefix: 'personal/fetchMyBurnoutLevel',
  endpoint: 'my_burnout_level',
  failureMessage: 'Failed to fetch personal burnout level',
  sendTimeZone: false,
});

export const fetchMyBurnoutMetrics = createPersonalThunk({
  key: 'burnoutMetrics',
  typePrefix: 'personal/fetchMyBurnoutMetrics',
  endpoint: 'my_metrics/burnout',
  failureMessage: 'Failed to fetch your burnout metrics',
  sendTimeZone: true,
});

export const fetchMyCheckIn = createPersonalThunk({
  key: 'checkIn',
  typePrefix: 'personal/fetchMyCheckIn',
  endpoint: 'my_metrics/check_in',
  failureMessage: 'Failed to fetch your check-in status',
  sendTimeZone: true,
});

export const fetchMyStressors = createPersonalThunk({
  key: 'stressors',
  typePrefix: 'personal/fetchMyStressors',
  endpoint: 'my_metrics/stressors',
  failureMessage: 'Failed to fetch your stressors',
  sendTimeZone: true,
});

export const fetchMyLearning = createPersonalThunk({
  key: 'learning',
  typePrefix: 'personal/fetchMyLearning',
  endpoint: 'my_metrics/learning',
  failureMessage: 'Failed to fetch your learning metrics',
  sendTimeZone: true,
});

// A hand-built action (as in the reducer tests) has no meta; it then matches an
// entry with no request recorded.
const requestIdOf = (action: { meta?: { requestId?: string } }): string | undefined => action.meta?.requestId;

const addPersonalCases = <K extends PersonalKey>(
  builder: ActionReducerMapBuilder<PersonalState>,
  key: K,
  thunk: PersonalThunk<PersonalPayload<K>>
) => {
  const entryOf = (state: PersonalState) => state[key] as AsyncEntry<PersonalPayload<K>>;
  builder
    .addCase(thunk.pending, (state, action) => {
      const entry = entryOf(state);
      entry.status = 'loading';
      entry.error = null;
      entry.requestId = requestIdOf(action);
    })
    .addCase(thunk.fulfilled, (state, action) => {
      const entry = entryOf(state);
      // A logout reset the entry (no request recorded) or a newer request owns it.
      if (entry.requestId !== requestIdOf(action)) return;
      entry.status = 'succeeded';
      entry.data = action.payload;
      delete entry.requestId;
    })
    .addCase(thunk.rejected, (state, action) => {
      const entry = entryOf(state);
      if (entry.requestId !== requestIdOf(action)) return;
      delete entry.requestId;
      if (action.payload === SESSION_CHANGED) {
        // Same store, new credentials (e.g. a fresh sign-in without a logout):
        // release the loading state so the next mount can ask again, and leave
        // what is stored untouched.
        entry.status = entry.data === null ? 'idle' : 'succeeded';
        return;
      }
      entry.status = 'failed';
      entry.error = action.payload ?? action.error.message ?? null;
    });
};

const LENS_KEYS = ['burnoutMetrics', 'checkIn', 'stressors', 'learning'] as const;

/**
 * Resets every my_metrics entry held for a lens other than `lensGroupId`, data
 * and request alike, so it reads as loading and the next mount asks again. A
 * response still in flight for the old lens then no longer matches the entry's
 * request and is dropped. The root reducer runs this after every action; with
 * no lens (always, outside a demo-lens build) it changes nothing.
 */
export const keepLens = (state: PersonalState, lensGroupId: number | null): PersonalState => {
  const stale = LENS_KEYS.filter((key) => (state[key].lensGroupId ?? null) !== lensGroupId);
  if (stale.length === 0) return state;
  const reset = () => (lensGroupId === null ? idleEntry() : { ...idleEntry(), lensGroupId });
  return { ...state, ...Object.fromEntries(stale.map((key) => [key, reset()])) };
};

const personalSlice = createSlice({
  name: 'personal',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addPersonalCases(builder, 'burnoutLevel', fetchMyBurnoutLevel);
    addPersonalCases(builder, 'burnoutMetrics', fetchMyBurnoutMetrics);
    addPersonalCases(builder, 'checkIn', fetchMyCheckIn);
    addPersonalCases(builder, 'stressors', fetchMyStressors);
    addPersonalCases(builder, 'learning', fetchMyLearning);
  },
});

/*
-------------------------------
SELECTORS (re-exported with RootState from _redux/index.ts)
-------------------------------
*/

export const getBurnoutMetrics = (state: PersonalState) => state.burnoutMetrics;
export const getCheckIn = (state: PersonalState) => state.checkIn;
export const getStressors = (state: PersonalState) => state.stressors;
export const getLearning = (state: PersonalState) => state.learning;

export default personalSlice.reducer;
